import type * as maplibregl from "maplibre-gl";
import { applyMapTheme } from "./mapTheme";
import { lineKey } from "../utils/lineKey";

// Статичная картинка маршрута для карточек: кусок нашей подложки с линией.
//
// Одна скрытая карта MapLibre на всё приложение (WebGL-контекстов у браузера
// мало, по карте на карточку нельзя), создаётся лениво при первом запросе и
// переиспользуется. Рендеры строго по очереди - карта одна, и пока один
// снимок ждёт idle, другой не должен сдвинуть ей вид. Готовые снимки
// кешируются; одинаковые запросы, пришедшие одновременно, делят один рендер.
// Любая неудача (нет WebGL, idle не дождались) отдаёт "" - карточка остаётся
// на SVG-заглушке, а очередь продолжает работать.

export type LonLat = [number, number];

export interface PreviewOptions {
  width: number;
  height: number;
  dark: boolean;
  color: string;
  // Перекрывает производный от линии ключ кеша (тема/размер/цвет всё равно
  // входят в ключ).
  cacheKey?: string;
  // Отмена ещё не начатого рендера (карточка ушла с экрана/размонтирована).
  // Рендер, который ждут и другие вызовы без отмены, всё равно выполняется.
  signal?: AbortSignal;
}

// Подмножество maplibregl.Map, которым пользуется рендерер, - чтобы тесты
// могли подсунуть фейковую карту без WebGL.
export type PreviewMapLike = Pick<
  maplibregl.Map,
  | "getContainer"
  | "resize"
  | "getLayer"
  | "addLayer"
  | "getSource"
  | "addSource"
  | "getPaintProperty"
  | "setPaintProperty"
  | "getLayoutProperty"
  | "setLayoutProperty"
  | "fitBounds"
  | "once"
  | "triggerRepaint"
  | "getCanvas"
>;

export interface RendererDeps {
  // Готовая карта: стиль загружен, слои подложки добавлены.
  createMap: () => Promise<PreviewMapLike>;
  applyTheme?: (map: PreviewMapLike, dark: boolean) => void;
  idleTimeoutMs?: number;
  cacheSize?: number;
}

const SOURCE = "route-preview";
const CASING_LAYER = "route-preview-casing";
const LINE_LAYER = "route-preview-line";
const ENDS_LAYER = "route-preview-ends";
const PADDING_RATIO = 0.12;

const keyOf = (line: LonLat[], o: PreviewOptions) =>
  JSON.stringify([o.cacheKey ?? lineKey(line), o.dark, o.width, o.height, o.color]);

const ensureRouteLayers = (map: PreviewMapLike) => {
  if (!map.getSource(SOURCE)) {
    map.addSource(SOURCE, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
  }
  if (!map.getLayer(CASING_LAYER)) {
    map.addLayer({
      id: CASING_LAYER,
      type: "line",
      source: SOURCE,
      filter: ["==", ["geometry-type"], "LineString"],
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#ffffff", "line-width": 7 },
    });
  }
  if (!map.getLayer(LINE_LAYER)) {
    map.addLayer({
      id: LINE_LAYER,
      type: "line",
      source: SOURCE,
      filter: ["==", ["geometry-type"], "LineString"],
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#047857", "line-width": 4 },
    });
  }
  if (!map.getLayer(ENDS_LAYER)) {
    map.addLayer({
      id: ENDS_LAYER,
      type: "circle",
      source: SOURCE,
      filter: ["==", ["geometry-type"], "Point"],
      // Старт - белый кружок с цветной обводкой, финиш - залитый цветом.
      paint: {
        "circle-radius": 5,
        "circle-stroke-width": 2.5,
        "circle-color": ["case", ["==", ["get", "role"], "start"], "#ffffff", "#047857"],
        "circle-stroke-color": ["case", ["==", ["get", "role"], "start"], "#047857", "#ffffff"],
      },
    });
  }
};

const routeData = (line: LonLat[]) => ({
  type: "FeatureCollection" as const,
  features: [
    { type: "Feature" as const, properties: {}, geometry: { type: "LineString" as const, coordinates: line } },
    { type: "Feature" as const, properties: { role: "start" }, geometry: { type: "Point" as const, coordinates: line[0] } },
    { type: "Feature" as const, properties: { role: "finish" }, geometry: { type: "Point" as const, coordinates: line[line.length - 1] } },
  ],
});

const boundsOf = (line: LonLat[]): [LonLat, LonLat] => {
  let [minX, minY] = line[0];
  let [maxX, maxY] = line[0];
  for (const [x, y] of line) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return [[minX, minY], [maxX, maxY]];
};

const waitIdle = (map: PreviewMapLike, timeoutMs: number) =>
  new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => resolve(false), timeoutMs);
    map.once("idle", () => {
      clearTimeout(timer);
      resolve(true);
    });
    map.triggerRepaint();
  });

export const createRenderer = (deps: RendererDeps) => {
  const applyTheme = deps.applyTheme ?? ((m, dark) => applyMapTheme(m as maplibregl.Map, dark));
  const idleTimeoutMs = deps.idleTimeoutMs ?? 8000;
  const cacheSize = deps.cacheSize ?? 150;
  // LRU: Map хранит порядок вставки - при попадании ключ переставляется в
  // конец, вытесняется первый.
  const cache = new Map<string, string>();
  const cacheGet = (key: string) => {
    const v = cache.get(key);
    if (v !== undefined) {
      cache.delete(key);
      cache.set(key, v);
    }
    return v;
  };
  const cachePut = (key: string, v: string) => {
    cache.delete(key);
    cache.set(key, v);
    while (cache.size > cacheSize) cache.delete(cache.keys().next().value as string);
  };
  interface Job {
    promise: Promise<string>;
    waiters: number;
    started: boolean;
    cancelled: boolean;
  }
  const inflight = new Map<string, Job>();
  let mapPromise: Promise<PreviewMapLike | null> | null = null;
  let queue: Promise<unknown> = Promise.resolve();

  const getMap = () =>
    (mapPromise ??= deps.createMap().then(
      (m) => m,
      (err) => {
        console.warn("[routePreview] map unavailable", err);
        return null;
      }
    ));

  const renderOnce = async (line: LonLat[], o: PreviewOptions): Promise<string> => {
    const map = await getMap();
    if (!map) return "";
    const container = map.getContainer();
    container.style.width = `${o.width}px`;
    container.style.height = `${o.height}px`;
    map.resize();
    applyTheme(map, o.dark);
    ensureRouteLayers(map);
    map.setPaintProperty(LINE_LAYER, "line-color", o.color);
    map.setPaintProperty(ENDS_LAYER, "circle-color", ["case", ["==", ["get", "role"], "start"], "#ffffff", o.color]);
    map.setPaintProperty(ENDS_LAYER, "circle-stroke-color", ["case", ["==", ["get", "role"], "start"], o.color, "#ffffff"]);
    (map.getSource(SOURCE) as maplibregl.GeoJSONSource).setData(routeData(line));
    const pad = Math.round(Math.min(o.width, o.height) * PADDING_RATIO);
    map.fitBounds(boundsOf(line), { padding: pad, animate: false, maxZoom: 16 });
    if (!(await waitIdle(map, idleTimeoutMs))) return "";
    return map.getCanvas().toDataURL("image/jpeg", 0.85);
  };

  const renderRoutePreview = (line: LonLat[] | null | undefined, o: PreviewOptions): Promise<string> => {
    if (!line || line.length < 2) return Promise.resolve("");
    // Чистая копия: линия обычно приходит реактивным Proxy из Pinia, а
    // setData пересылает данные в воркер MapLibre через postMessage, который
    // Proxy не клонирует - линия молча не рисовалась (подложка без маршрута).
    const plain = line.map((p) => [Number(p[0]), Number(p[1])] as LonLat);
    const key = keyOf(plain, o);
    const hit = cacheGet(key);
    if (hit !== undefined) return Promise.resolve(hit);
    let job = inflight.get(key);
    if (!job) {
      const j: Job = { promise: Promise.resolve(""), waiters: 0, started: false, cancelled: false };
      j.promise = queue
        .then(() => {
          // Все ждавшие отказались до начала - карту не трогаем.
          if (j.cancelled) return "";
          j.started = true;
          return renderOnce(plain, o);
        })
        .catch((err) => {
          console.warn("[routePreview] render failed", err);
          return "";
        })
        .then((url) => {
          if (inflight.get(key) === j) inflight.delete(key);
          if (url) cachePut(key, url);
          return url;
        });
      queue = j.promise;
      inflight.set(key, j);
      job = j;
    }
    const j = job;
    j.waiters += 1;
    const signal = o.signal;
    if (!signal) return j.promise;
    if (signal.aborted) {
      release(key, j);
      return Promise.resolve("");
    }
    return new Promise<string>((resolve) => {
      const onAbort = () => {
        release(key, j);
        resolve("");
      };
      signal.addEventListener("abort", onAbort, { once: true });
      j.promise.then((url) => {
        signal.removeEventListener("abort", onAbort);
        resolve(url);
      });
    });
  };

  const release = (key: string, j: Job) => {
    j.waiters -= 1;
    if (j.waiters > 0 || j.started) return;
    j.cancelled = true;
    // Новый запрос того же ключа поставит свежую задачу, а не отменённую.
    if (inflight.get(key) === j) inflight.delete(key);
  };

  return { renderRoutePreview };
};

// Экземпляр приложения: карта создаётся в отдельном модуле (тянет maplibre-gl
// и всю подложку), импортируется только при первом запросе картинки.
const appRenderer = createRenderer({
  createMap: () => import("./routePreviewMap").then((m) => m.createPreviewMap()),
});

export const renderRoutePreview = appRenderer.renderRoutePreview;
